import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';

// Windows 95 themed components
const Win95Window = ({ title, children, className, isMinimized = false, onClose, onMinimize, onMaximize }) => {
  if (isMinimized) return null;
  
  return (
    <div className={`border-2 border-gray-300 bg-gray-200 shadow-win95 ${className}`}>
      <div className="flex items-center bg-win95-blue text-white py-1 px-1">
        <div className="ml-1 mr-auto font-bold text-sm">
          {title}
        </div>
        <div className="flex">
          {onMinimize && (
            <button onClick={onMinimize} className="border-2 border-gray-300 w-5 h-5 mr-1 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-black">
              <span className="mb-1 text-xs">_</span>
            </button>
          )}
          {onMaximize && (
            <button onClick={onMaximize} className="border-2 border-gray-300 w-5 h-5 mr-1 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-black">
              <span className="text-xs">□</span>
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="border-2 border-gray-300 w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-black">
              <span className="text-xs">✕</span>
            </button>
          )}
        </div>
      </div>
      <div className="p-2">
        {children}
      </div>
    </div>
  );
};

const Win95Button = ({ onClick, children, className, active }) => {
  return (
    <button 
      onClick={onClick}
      className={`
        px-4 py-1 border-2 
        ${active 
          ? 'border-t-gray-600 border-l-gray-600 border-b-white border-r-white bg-gray-300' 
          : 'border-t-white border-l-white border-b-gray-600 border-r-gray-600 bg-gray-200 hover:bg-gray-300'
        }
        font-bold text-sm
        ${className}
      `}
    >
      {children}
    </button>
  );
};

const Win95Taskbar = ({ activeTeamMember, setActiveTeamMember, teamMembers }) => {
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="fixed bottom-0 left-0 right-0 h-10 bg-gray-200 border-t-2 border-white flex items-center px-1 z-50">
      <div className="relative">
        <Win95Button 
          onClick={() => setShowStartMenu(!showStartMenu)} 
          active={showStartMenu}
          className="flex items-center gap-2"
        >
          <img src="/win95-logo.png" alt="Start" className="w-4 h-4" />
          <span>Start</span>
        </Win95Button>
        
        {showStartMenu && (
          <div className="absolute bottom-10 left-0 w-64 border-2 border-gray-300 shadow-win95 bg-gray-200">
            <div className="bg-win95-blue h-6 px-2 flex items-center">
              <span className="text-white font-bold text-sm transform rotate-270 whitespace-nowrap">4FingerStudios</span>
            </div>
            <div className="p-1">
              {teamMembers.map((member, index) => (
                <div 
                  key={index} 
                  className="p-1 hover:bg-win95-blue hover:text-white cursor-pointer flex items-center"
                  onClick={() => {
                    setActiveTeamMember(index);
                    setShowStartMenu(false);
                  }}
                >
                  <div className="w-6 h-6 bg-gray-300 border border-gray-400 mr-2"></div>
                  <span className="text-sm">{member.name}</span>
                </div>
              ))}
              <div className="border-t border-gray-400 my-1"></div>
              <div className="p-1 hover:bg-win95-blue hover:text-white cursor-pointer flex items-center">
                <div className="w-6 h-6 bg-gray-300 border border-gray-400 mr-2"></div>
                <span className="text-sm">About 4FingerStudios</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex-1 flex items-center px-2">
        {teamMembers.map((member, index) => (
          activeTeamMember === index && (
            <Win95Button 
              key={index} 
              active={true}
              className="flex items-center gap-2 ml-2"
              onClick={() => setActiveTeamMember(null)}
            >
              <div className="w-4 h-4 bg-gray-300 border border-gray-400"></div>
              <span className="truncate max-w-32">{member.name}</span>
            </Win95Button>
          )
        ))}
      </div>
      
      <div className="border-2 border-t-gray-600 border-l-gray-600 border-b-white border-r-white px-2 py-1 text-sm">
        {currentTime}
      </div>
    </div>
  );
};

const Win95TeamMember = ({ member, onClose }) => {
  const [activeTab, setActiveTab] = useState('experience');
  
  const tabs = [
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'contact', label: 'Contact' },
  ];
  
  return (
    <Win95Window 
      title={`${member.name} - Resume.exe`} 
      className="w-full max-w-4xl mx-auto mt-6 mb-16" 
      onClose={onClose}
    >
      <div className="flex items-start">
        <div className="w-24 h-24 bg-gray-300 border border-gray-400 mr-4 flex-shrink-0">
          {member.image ? (
            <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <span>Photo</span>
            </div>
          )}
        </div>
        
        <div>
          <h2 className="text-xl font-bold">{member.name}</h2>
          <p className="text-sm font-sans">{member.title}</p>
          <p className="text-sm font-sans mt-2">{member.summary}</p>
        </div>
      </div>
      
      <div className="mt-4 border-b border-gray-400">
        <div className="flex">
          {tabs.map(tab => (
            <Win95Button 
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="rounded-t-md rounded-b-none mr-1"
            >
              {tab.label}
            </Win95Button>
          ))}
        </div>
      </div>
      
      <div className="mt-4 p-2 border-2 border-gray-600 bg-white h-64 overflow-y-auto font-sans text-sm">
        {activeTab === 'experience' && (
          <div>
            <h3 className="font-bold underline mb-2">Work Experience</h3>
            {member.experience.map((exp, index) => (
              <div key={index} className="mb-3">
                <div className="flex justify-between">
                  <strong>{exp.role}</strong>
                  <span>{exp.period}</span>
                </div>
                <div>{exp.company}</div>
                <ul className="list-disc pl-5 mt-1">
                  {exp.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'skills' && (
          <div>
            <h3 className="font-bold underline mb-2">Technical Skills</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(member.skills).map(([category, skills], index) => (
                <div key={index} className="mb-3">
                  <strong>{category}:</strong>
                  <div className="ml-2">{skills.join(', ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'projects' && (
          <div>
            <h3 className="font-bold underline mb-2">Notable Projects</h3>
            {member.projects.map((project, index) => (
              <div key={index} className="mb-3">
                <strong>{project.name}</strong>
                <div>{project.description}</div>
                {project.link && (
                  <div className="text-win95-blue underline cursor-pointer">
                    {project.link}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'contact' && (
          <div>
            <h3 className="font-bold underline mb-2">Contact Information</h3>
            <div className="grid grid-cols-1 gap-2">
              <div><strong>Email:</strong> {member.contact.email}</div>
              {member.contact.phone && <div><strong>Phone:</strong> {member.contact.phone}</div>}
              {member.contact.social && (
                <div>
                  <strong>Social:</strong>
                  <div className="ml-2">
                    {Object.entries(member.contact.social).map(([platform, link], index) => (
                      <div key={index} className="text-win95-blue underline cursor-pointer">
                        {platform}: {link}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Win95Window>
  );
};

const HomePage = () => {


  return (
    <div className="min-h-screen bg-win95-teal font-ms-sans text-black overflow-x-hidden pb-12">
      {/* Win95 Background Pattern */}
      <div className="fixed inset-0 bg-win95-pattern opacity-5 pointer-events-none"></div>
      
      {/* Win95 Icons on Desktop */}
      <div className="pt-2 pl-2 grid grid-cols-6 gap-4">
        {teamMembers.map((member, index) => (
          <div 
            key={index}
            className="flex flex-col items-center w-20 cursor-pointer hover:bg-win95-blue hover:bg-opacity-30 p-1"
            onClick={() => setActiveTeamMember(index)}
          >
            <div className="w-12 h-12 bg-gray-300 border border-gray-400 flex items-center justify-center text-xs">
              <span>📄</span>
            </div>
            <span className="text-xs text-center mt-1">{member.name}.exe</span>
          </div>
        ))}
        
        <div 
          className="flex flex-col items-center w-20 cursor-pointer hover:bg-win95-blue hover:bg-opacity-30 p-1"
          onClick={() => setShowAboutStudio(true)}
        >
          <div className="w-12 h-12 bg-gray-300 border border-gray-400 flex items-center justify-center text-xs">
            <span>🏢</span>
          </div>
          <span className="text-xs text-center mt-1">About.exe</span>
        </div>
      </div>
      
      {/* About Studio Window */}
      {showAboutStudio && (
        <Win95Window 
          title="About 4FingerStudios" 
          className="w-full max-w-4xl mx-auto mt-6 mb-16" 
          onClose={() => setShowAboutStudio(false)}
        >
          <div className="p-2">
            {/* Studio Logo */}
            <div className="flex justify-center mb-4">
              <div className="w-24 h-24 bg-gray-300 border border-gray-400 flex items-center justify-center">
                <span className="text-2xl font-bold">4F</span>
              </div>
            </div>
            
            {/* Studio Name */}
            <h2 className="text-center text-2xl font-bold mb-4">4FingerStudios</h2>
            
            {/* Tabs for Studio Info */}
            <div className="border-2 border-gray-600 bg-white p-3 font-sans text-sm">
              <h3 className="font-bold underline mb-4">Our Studio History</h3>
              
              {studioTimeline.map((item, index) => (
                <div key={index} className="mb-4 pl-2 border-l-4 border-win95-blue">
                  <div className="font-bold">{item.period}</div>
                  <div>{item.content}</div>
                </div>
              ))}
            </div>
            
            {/* Social Links */}
        
            
            {/* Copyright */}
            <div className="mt-4 text-center text-xs">
              <p>© {new Date().getFullYear()} 4FingerStudios. All rights reserved.</p>
            </div>
          </div>
        </Win95Window>
      )}
      
      {/* Team Member Window */}
      {activeTeamMember !== null && (
        <Win95TeamMember 
          member={teamMembers[activeTeamMember]} 
          onClose={() => setActiveTeamMember(null)} 
        />
      )}
      
      {/* Win95 Taskbar */}
      <Win95Taskbar 
        activeTeamMember={activeTeamMember}
        setActiveTeamMember={setActiveTeamMember}
        teamMembers={teamMembers}
      />
    </div>
  );
};

export default HomePage;